import { useMemo, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton } from "../../controls";
import { getFromDataPath } from "../../utility";
import { AnalysisOutputProgrammingCode, DocumentReference } from "../ars_ldm";
import { useArsContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { ArsSelect, ArsText } from "../inputs";
import { isAnalysis } from "../typeGuards/isAnalysis";
import { AnalysisOutputProgrammingCodeControl } from "./AnalysisOutputProgrammingCodeControl";
import { AnalysisOutputProgrammingCodeParametersControl } from "./AnalysisOutputProgrammingCodeParametersControl";
import { DocumentReferenceControl } from "./DocumentReferenceControl";
import { NamedObjectControl } from "./NamedObjectControl";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Builds dropdown options for ARS entity ID selectors.
 *
 * Includes the current value when it is not present in state to avoid
 * rendering an empty selection for legacy or out-of-sync data.
 *
 * @param records - Available ARS entities from context state.
 * @param selectedId - Currently selected ID on the edited analysis.
 * @returns Select options for use with ArsSelect.
 */
const buildIdOptions = <T extends { id: string; name?: string }>(
  records: Record<string, T>,
  selectedId: string,
): { value: string; label: string }[] => {
  const entityIds = Object.keys(records);
  const knownIds = [...entityIds, ...[selectedId].filter((id) => id && !entityIds.includes(id))];

  return knownIds.map((id) => {
    const name = records[id]?.name ?? id;
    return { value: id, label: `${name} (${id})` };
  });
};

/**
 * Props for the AnalysisControl component.
 */
export interface AnalysisControlProps {
  /** RFC 6901 JSON Pointer path to the Analysis in ArsContext state. */
  dataPath: string;
  /** Can rename the object. */
  canRename?: boolean;
  /** Disabled state. */
  disabled?: boolean;
}

/**
 * A component for managing Analysis entities.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the Analysis in ArsContext state.
 * @param props.canRename - Optional flag to allow renaming and removing the object.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the analysis editor.
 */
export const AnalysisControl = ({
  dataPath,
  canRename = false,
  disabled = false,
}: AnalysisControlProps): React.ReactElement => {
  const { state, dispatch } = useArsContext();
  const [activeTab, setActiveTab] = useState<string>("properties");

  const analysis = getFromDataPath(dataPath, state, isAnalysis);
  const documentReferences: DocumentReference[] = useMemo(
    () =>
      analysis?.documentRefs ??
      // istanbul ignore next
      [],
    [analysis],
  );
  const analysisSetOptions = useMemo(
    () =>
      buildIdOptions(
        state.analysisSets,
        analysis?.analysisSetId ??
          // istanbul ignore next
          "",
      ),
    [state.analysisSets, analysis?.analysisSetId],
  );
  const dataSubsetOptions = useMemo(
    () =>
      buildIdOptions(
        state.dataSubsets,
        analysis?.dataSubsetId ??
          // istanbul ignore next
          "",
      ),
    [state.dataSubsets, analysis?.dataSubsetId],
  );
  const methodOptions = useMemo(
    () =>
      buildIdOptions(
        state.analysisMethods,
        analysis?.methodId ??
          // istanbul ignore next
          "",
      ),
    [state.analysisMethods, analysis?.methodId],
  );
  const hasProgrammingCode = analysis?.programmingCode !== undefined;

  /**
   * Handles adding a new document reference to the analysis.
   */
  const handleAddDocumentReference = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && analysis) {
      const documentReferenceCount = documentReferences.length;
      const newDocumentReference: DocumentReference = {
        referenceDocumentId: "",
        pageRefs: [],
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/documentRefs/${documentReferenceCount}`,
        value: newDocumentReference,
      });
    }
  };

  /**
   * Handles adding a programming code block to the analysis.
   */
  const handleAddProgrammingCode = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && analysis && !hasProgrammingCode) {
      const newProgrammingCode: AnalysisOutputProgrammingCode = {
        context: "",
        code: "",
        parameters: [],
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/programmingCode`,
        value: newProgrammingCode,
      });
    }
  };

  const idRoute = dataPath.replace(/\//g, "-");

  return (
    <div className="border rounded p-3 mb-3 position-relative">
      {canRename && (
        <div className="position-absolute top-0 end-0 m-2">
          <RemoveArsItemButton
            dataPath={dataPath}
            disabled={disabled}
          />
        </div>
      )}

      <Tab.Container
        activeKey={activeTab}
        onSelect={(k) => {
          /* istanbul ignore else */
          if (k) {
            setActiveTab(k);
          }
        }}
      >
        <Nav
          variant="tabs"
          className="mb-3"
        >
          <Nav.Item>
            <Nav.Link eventKey="properties">Properties</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="data-selection">Data selection</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="document-refs">Document references</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="programming-code">Programming code</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="programming-parameters">Programming parameters</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="properties">
            <NamedObjectControl
              dataPath={dataPath}
              canRename={canRename}
              disabled={disabled}
            />

            <ArsText
              dataPath={`${dataPath}/reason/controlledTerm`}
              id={`${idRoute}-reason-controlled-term`}
              label="Reason controlled term"
              labelWidth={2}
              placeholder="Enter reason controlled term"
              disabled={disabled}
            />
            <ArsText
              dataPath={`${dataPath}/purpose/controlledTerm`}
              id={`${idRoute}-purpose-controlled-term`}
              label="Purpose controlled term"
              labelWidth={2}
              placeholder="Enter purpose controlled term"
              disabled={disabled}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="data-selection">
            <ArsText
              dataPath={`${dataPath}/dataset`}
              id={`${idRoute}-dataset`}
              label="Dataset"
              labelWidth={2}
              placeholder="Enter dataset"
              disabled={disabled}
            />
            <ArsText
              dataPath={`${dataPath}/variable`}
              id={`${idRoute}-variable`}
              label="Variable"
              labelWidth={2}
              placeholder="Enter variable"
              disabled={disabled}
            />
            <ArsSelect
              dataPath={`${dataPath}/analysisSetId`}
              id={`${idRoute}-analysis-set-id`}
              label="Analysis set id"
              labelWidth={2}
              options={analysisSetOptions}
              disabled={disabled}
            />
            <ArsSelect
              dataPath={`${dataPath}/dataSubsetId`}
              id={`${idRoute}-data-subset-id`}
              label="Data subset id"
              labelWidth={2}
              options={dataSubsetOptions}
              disabled={disabled}
            />
            <ArsSelect
              dataPath={`${dataPath}/methodId`}
              id={`${idRoute}-method-id`}
              label="Method id"
              labelWidth={2}
              options={methodOptions}
              disabled={disabled}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="document-refs">
            {documentReferences.map((documentReference, index) => (
              <DocumentReferenceControl
                key={`${documentReference.referenceDocumentId}-${index}`}
                dataPath={`${dataPath}/documentRefs/${index}`}
                disabled={disabled}
                siblingCount={documentReferences.length}
              />
            ))}

            <AsyncButton
              id={`${idRoute}-add-document-reference`}
              onClick={handleAddDocumentReference}
              className="d-flex align-items-center btn-sm btn-success"
              disabled={disabled}
            >
              <span className="me-2">Add document reference</span>
              <PlusCircle />
            </AsyncButton>
          </Tab.Pane>

          <Tab.Pane eventKey="programming-code">
            {hasProgrammingCode && (
              <AnalysisOutputProgrammingCodeControl
                dataPath={`${dataPath}/programmingCode`}
                disabled={disabled}
              />
            )}

            <AsyncButton
              id={`${idRoute}-add-programming-code`}
              onClick={handleAddProgrammingCode}
              className="d-flex align-items-center btn-sm btn-success"
              disabled={disabled || hasProgrammingCode}
            >
              <span className="me-2">Add programming code</span>
              <PlusCircle />
            </AsyncButton>
          </Tab.Pane>

          <Tab.Pane eventKey="programming-parameters">
            {hasProgrammingCode && (
              <AnalysisOutputProgrammingCodeParametersControl
                dataPath={`${dataPath}/programmingCode`}
                disabled={disabled}
              />
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

AnalysisControl.displayName = "AnalysisControl";
